import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AbilityProvider, Can as CaslCan } from '@casl/react';
import { ApiError, onUnauthorized } from '@/api/client';
import { meKey, useMe } from '@/api/auth';
import { getActiveWorkspaceId, setActiveWorkspaceId } from '@/api/workspace-scope';
import { ErrorState, FullPageSpinner } from '@/components/ui';
import { buildAbility, type AbilityAction, type AbilitySubject } from './ability';
import { AuthContext, useAuth, type AuthValue } from './useAuth';

/** Gate for authenticated routes: loads /auth/me, redirects to /login when there's no session. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const me = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(
    () =>
      onUnauthorized(() => {
        qc.clear();
        navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
      }),
    [qc, navigate, location.pathname, location.search],
  );

  // Switching workspace changes every tenant query, so start from an empty cache.
  const switchWorkspace = useCallback(
    (id: string | null, to: string | null) => {
      setActiveWorkspaceId(id);
      // Drop every cached tenant query and reload who-am-I for the new scope.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== meKey[0] });
      void qc.resetQueries({ queryKey: meKey });
      if (to) navigate(to, { replace: true });
    },
    [qc, navigate],
  );

  // Only super admins choose a workspace; never leak a stored choice into someone else's session.
  useEffect(() => {
    if (me.data && me.data.role !== 'super_admin' && getActiveWorkspaceId()) setActiveWorkspaceId(null);
  }, [me.data]);
  // The workspace a super admin had open no longer exists (API answers 404): forget it and reload.
  const staleWorkspace =
    me.error instanceof ApiError && me.error.code === 'NOT_FOUND' && !!getActiveWorkspaceId();
  useEffect(() => {
    if (!staleWorkspace) return;
    setActiveWorkspaceId(null);
    void me.refetch();
  }, [staleWorkspace, me]);

  const value = useMemo<AuthValue | null>(() => {
    if (!me.data) return null;
    const ability = buildAbility(me.data.rules);
    return {
      user: me.data,
      workspace: me.data.workspace,
      isPlatformAdmin: me.data.role === 'super_admin',
      ability,
      can: (action, subject) => ability.can(action, subject),
      enterWorkspace: (id) => switchWorkspace(id, '/inbox'),
      leaveWorkspace: (opts) => switchWorkspace(null, opts?.navigate === false ? null : '/admin/clients'),
    };
  }, [me.data, switchWorkspace]);

  if (me.isPending) return <FullPageSpinner label="Loading your workspace" />;
  if (me.error) {
    if (me.error instanceof ApiError && me.error.status === 401) {
      return (
        <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />
      );
    }
    if (staleWorkspace) return <FullPageSpinner label="Loading your workspace" />;
    return <ErrorState error={me.error} onRetry={() => void me.refetch()} />;
  }
  return (
    <AuthContext.Provider value={value}>
      <AbilityProvider value={value!.ability}>{children}</AbilityProvider>
    </AuthContext.Provider>
  );
}

/** Renders children only if the current ability allows `I` on `a`, e.g. `<Can I="create" a="Ticket">`. */
export function Can({
  I,
  a,
  children,
  fallback = null,
}: {
  I: AbilityAction;
  a: AbilitySubject;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return (
    <CaslCan I={I} a={a} passThrough>
      {({ isAllowed }) => (isAllowed ? children : fallback)}
    </CaslCan>
  );
}

/** Route guard: shows a "no access" state instead of a page the role can't use. */
export function RequireAbility({
  I,
  a,
  children,
}: {
  I: AbilityAction;
  a: AbilitySubject;
  children: ReactNode;
}) {
  const { can } = useAuth();
  if (!can(I, a)) return <Navigate to="/" replace />;
  return <>{children}</>;
}
